import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, REQUEST } from '@angular/core';
import { environment } from '@environments/environment';
import { CurrentLanguageService } from '@shared/services/language/current-language.service';
import { envConfiguredHostname } from '@shared/utils';

/**
 * Service to determine the current platform (browser or server) in an Angular application.
 * This service uses Angular's dependency injection to access the platform ID and provides
 * utility methods for platform detection.
 */
@Injectable({ providedIn: 'root' })
export class PlatformContextService {
  private readonly platformId = inject<string>(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly request = inject(REQUEST, { optional: true });
  private readonly region = inject(CurrentLanguageService).language.region;

  /**
   * Checks whether the application is running in a browser environment.
   *
   * @returns {boolean} Returns `true` if the platform is a browser, otherwise `false`.
   */
  isBrowser = (): boolean => isPlatformBrowser(this.platformId);

  /**
   * Checks whether the application is running in a server environment.
   *
   * @returns {boolean} Returns `true` if the platform is server-rendered (Angular Universal), otherwise `false`.
   */
  isServer = (): boolean => isPlatformServer(this.platformId);

  /**
   * Returns the hostname of the current request context.
   * - In SSR: parses the incoming HTTP Request URL (REQUEST injection token).
   * - In CSR: reads `document.location.hostname`.
   * Returns an empty string when neither source is available.
   */
  getHostname = (): string => {
    if (this.isServer()) {
      return this.request ? new URL(this.request.url).hostname : '';
    }
    return this.document.location?.hostname ?? '';
  };

  /**
   * Returns the origin (`<scheme>://<host>[:<port>]`) of the current request context.
   * - In SSR: parses the incoming HTTP Request URL (REQUEST injection token).
   * - In CSR: reads `document.location.origin`.
   * Returns an empty string when neither source is available. Useful for callers that need to
   * build absolute URLs (e.g. HTTP fetch endpoints) without hardcoding a host per environment.
   */
  getOrigin = (): string => {
    if (this.isServer()) {
      return this.request ? new URL(this.request.url).origin : '';
    }
    return this.document.location?.origin ?? '';
  };

  /**
   * Whether CMS content is read from the Drupal API rather than from the prefetch JSONs, which only the static site
   * serves, at the public address of the region. See « Source du contenu CMS » in `docs/architecture.md`.
   */
  readsDrupal = (): boolean => {
    if (this.isServer()) return true;
    if (environment.applicationType === 'LOCAL') return !environment.prefetchOverrideBaseUrl;
    return this.getOrigin() !== envConfiguredHostname(this.region);
  };

  /** Where the prefetch JSONs are read: the page origin, or the static site a local build points at. */
  prefetchBaseUrl = (): string =>
    (environment.applicationType === 'LOCAL' && environment.prefetchOverrideBaseUrl) || this.getOrigin();
}
